import React, { useEffect, useRef, useState, useCallback } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Messages } from "primereact/messages";
import { PrimeIcons } from "primereact/api";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { Tag } from "primereact/tag";
import { BreadCrumb } from "primereact/breadcrumb";
import { BaseApiServiceImpl } from "../app_utils/api/BaseApiServiceImpl";
import { PaymentService } from "../app_utils/api/PaymentService";
import { detectNetwork, NetworkDetectionResult } from "../app_utils/api/NetworkDetectionService";
import { MessageUtils } from "../app_utils/utils/MessageUtils";
import { formatAmountWithCommas } from "../app_utils/utils/Utils";
import { HOME_ROUTE_PATH } from "../app_utils/route_paths/resolver/PageRoutes";
import { UserSessionUtils } from "../app_utils/utils/UserSessionUtils";

const NETWORKS = [
  { name: "MTN", value: "MTN" },
  { name: "Airtel", value: "AIRTEL" },
];

const WALLET_DEPOSIT_MODES = [
  { name: "Mobile Money Payment", value: "MobileMoneyPayment" },
  { name: "Cash Payment", value: "CashPayment" },
  { name: "Bank Payment", value: "BankPayment" },
  { name: "Freebie", value: "Freebie" },
  { name: "Testing Credits", value: "TestingCredits" },
  { name: "System", value: "System" },
];

const Wallet = () => {
  const [records, setRecords] = useState<any>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(10);
  const [walletBalance, setWalletBalance] = useState<number>(0);
  const [smsCredits, setSmsCredits] = useState<number>(0);
  const [emailCredits, setEmailCredits] = useState<number>(0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [reverseDialogVisible, setReverseDialogVisible] = useState(false);
  const [recordToReverse, setRecordToReverse] = useState<any>(null);
  const [reversalReason, setReversalReason] = useState("");
  const [isReversing, setIsReversing] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [recordToCheck, setRecordToCheck] = useState<any>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const networkDetectRef = useRef<any>(null);
  const [detectedNetwork, setDetectedNetwork] = useState<NetworkDetectionResult | null>(null);

  // Top-up form state
  const [amount, setAmount] = useState<number | null>(null);
  const [msisdn, setMsisdn] = useState("");
  const [network, setNetwork] = useState<string>("");
  const [customerName, setCustomerName] = useState("");
  const [narration, setNarration] = useState("Wallet Top-up");
  const [isProcessing, setIsProcessing] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [paymentResponse, setPaymentResponse] = useState<any>(null);
  const [transactionStatus, setTransactionStatus] = useState<string>("");
  const [lookupResult, setLookupResult] = useState<string>("");
  const [isLookingUp, setIsLookingUp] = useState(false);
  const pollingRef = useRef<any>(null);

  const message = useRef<any>();
  const toast = useRef<Toast>(null);

  const userId = UserSessionUtils.getUserDetails()?.id;
  const isSuperAdmin = UserSessionUtils.isSuperAdmin();
  const userName = UserSessionUtils.getUserDetails()?.firstName;

  const fetchWalletBalance = () => {
    if (!userId) return;
    new BaseApiServiceImpl("/api/v1/wallet-deposits/balance-info/" + userId)
      .getRequestWithJsonResponse({})
      .then((response) => {
        setWalletBalance(response?.data?.balance ?? 0);
        setSmsCredits(response?.data?.smsCredits ?? response?.data?.credits ?? 0);
        setEmailCredits(response?.data?.emailCredits ?? 0);
      })
      .catch(() => {});
  };

  const fetchRecords = () => {
    setLoading(true);
    new BaseApiServiceImpl("/api/v1/wallet-deposits")
      .getRequestWithJsonResponse({ offset, limit })
      .then((response) => {
        setRecords(response?.records || []);
        setTotalRecords(response?.totalRecords || 0);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        MessageUtils.showErrorMessage(message, error.message);
      });
  };

  useEffect(() => {
    fetchRecords();
    fetchWalletBalance();
    if (userName) {
      setCustomerName(userName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, userId, userName]);

  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (networkDetectRef.current) clearTimeout(networkDetectRef.current);
    if (!msisdn || msisdn.length < 6) {
      setDetectedNetwork(null);
      return;
    }
    networkDetectRef.current = setTimeout(async () => {
      const result = await detectNetwork(msisdn);
      if (result) {
        setDetectedNetwork(result);
        setNetwork(result.network);
      } else {
        setDetectedNetwork(null);
      }
    }, 300);
    return () => {
      if (networkDetectRef.current) clearTimeout(networkDetectRef.current);
    };
  }, [msisdn]);

  const openNewManualDeposit = () => {
    setSelectedRecord(null);
    setIsDialogOpen(true);
  };

  const onPageChange = (event: any) => {
    setOffset(event.first);
  };

  const openReverseDialog = (record: any) => {
    setRecordToReverse(record);
    setReversalReason("");
    setReverseDialogVisible(true);
  };

  const confirmReverse = () => {
    if (!recordToReverse) return;
    if (!reversalReason.trim()) {
      MessageUtils.showErrorMessage(message, "Reversal reason is required");
      return;
    }

    setIsReversing(true);
    new BaseApiServiceImpl(
      `/api/v1/wallet-deposits/${recordToReverse.id}/reverse?reversalReason=${encodeURIComponent(reversalReason)}`
    )
      .postRequestWithJsonResponse({})
      .then(() => {
        setIsReversing(false);
        setReverseDialogVisible(false);
        setRecordToReverse(null);
        MessageUtils.showSuccessMessage(message, "Deposit reversed successfully");
        fetchRecords();
        fetchWalletBalance();
      })
      .catch((error) => {
        setIsReversing(false);
        MessageUtils.showErrorMessage(message, error.message);
      });
  };

  const validateTopUpForm = (): boolean => {
    if (!amount || amount < 500) {
      toast.current?.show({
        severity: "error",
        summary: "Validation Error",
        detail: "Minimum top-up amount is 500 UGX.",
      });
      return false;
    }
    if (!msisdn.trim()) {
      toast.current?.show({
        severity: "error",
        summary: "Validation Error",
        detail: "Please enter your mobile money phone number.",
      });
      return false;
    }
    const cleaned = msisdn.trim().replace(/[^0-9+]/g, "");
    const phoneRegex = cleaned.startsWith("+")
      ? /^\+[1-9]\d{6,14}$/
      : /^\d{7,15}$/;
    if (!phoneRegex.test(cleaned)) {
      toast.current?.show({
        severity: "error",
        summary: "Validation Error",
        detail: "Invalid phone number. Use E.164 format (e.g., 256772123456).",
      });
      return false;
    }
    if (!network) {
      toast.current?.show({
        severity: "error",
        summary: "Validation Error",
        detail: "Please select a mobile money network.",
      });
      return false;
    }
    if (!customerName.trim()) {
      toast.current?.show({
        severity: "error",
        summary: "Validation Error",
        detail: "Please enter your name.",
      });
      return false;
    }
    return true;
  };

  const detectNetworkFromMsisdn = (value: string): string | null => {
    const digits = value.replace(/[^0-9]/g, "");
    if (digits.startsWith("25678") || digits.startsWith("25677")) {
      return "MTN";
    }
    if (digits.startsWith("25675") || digits.startsWith("25670") || digits.startsWith("25674")) {
      return "AIRTEL";
    }
    return null;
  };

  useEffect(() => {
    if (!msisdn || !msisdn.trim()) {
      return;
    }
    const detected = detectNetworkFromMsisdn(msisdn);
    if (detected && detected !== network) {
      setNetwork(detected);
    }
  }, [msisdn, network]);

  const handleLookup = async () => {
    if (!msisdn.trim() || !network) {
      toast.current?.show({
        severity: "warn",
        summary: "Warning",
        detail: "Enter phone number and select network first.",
      });
      return;
    }
    setIsLookingUp(true);
    try {
      const wrapped = await PaymentService.lookupAccount(msisdn, network);
      const result = wrapped;
      if (result?.success && result?.accountName) {
        setLookupResult(result.accountName);
        setCustomerName(result.accountName);
        toast.current?.show({
          severity: "success",
          summary: "Account Verified",
          detail: `Account name: ${result.accountName}`,
        });
      } else {
        setLookupResult("");
        toast.current?.show({
          severity: "warn",
          summary: "Not Found",
          detail: "Could not verify account name.",
        });
      }
    } catch (error: any) {
      setLookupResult("");
      toast.current?.show({
        severity: "error",
        summary: "Lookup Failed",
        detail: error.message || "Account lookup failed.",
      });
    } finally {
      setIsLookingUp(false);
    }
  };

  const handleTopUp = async () => {
    if (!validateTopUpForm()) return;

    setIsProcessing(true);
    try {
      const wrapped = await PaymentService.initiateCollection({
        customerName,
        narration,
        currency: "UGX",
        amount: Math.round(amount!),
        msisdn: msisdn.replace(/[^0-9]/g, ""),
        network,
      });
      const response = wrapped;

      setPaymentResponse(response);
      setTransactionStatus(response?.status || "pending");
      setShowStatusDialog(true);

      if (response?.success) {
        toast.current?.show({
          severity: "success",
          summary: "Payment Initiated",
          detail: response?.message,
        });
        startStatusPolling(response?.reference);
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Payment Failed",
          detail: response?.message,
        });
        setShowStatusDialog(false);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.message || "Payment initiation failed.",
      });
      setShowStatusDialog(false);
    } finally {
      setIsProcessing(false);
    }
  };

  const startStatusPolling = (reference: string) => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }
    pollingRef.current = setInterval(async () => {
      try {
        const wrapped = await PaymentService.checkTransactionStatus(reference);
        const status = wrapped;
        if (status?.status) {
          setTransactionStatus(status.status);
          if (status.status === "COMPLETED" || status.status === "FAILED") {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
            if (status.status === "COMPLETED") {
              toast.current?.show({
                severity: "success",
                summary: "Payment Completed",
                detail: "Your wallet has been credited successfully!",
              });
              fetchWalletBalance();
            } else {
              toast.current?.show({
                severity: "error",
                summary: "Payment Failed",
                detail: "The payment transaction failed.",
              });
            }
            fetchRecords();
          }
        }
      } catch (error) {
        console.error("Status check failed:", error);
      }
    }, 5000);
  };

  const checkPaymentStatus = (record: any) => {
    if (!record?.transactionReference) {
      MessageUtils.showErrorMessage(message, "No transaction reference found");
      return;
    }
    setIsCheckingStatus(true);
    setRecordToCheck(record);
    PaymentService.checkTransactionStatus(record.transactionReference)
      .then((wrapped) => {
        const status = wrapped;
        setIsCheckingStatus(false);
        setRecordToCheck(null);
        if (status?.status === "COMPLETED") {
          MessageUtils.showSuccessMessage(message, "Payment completed. Wallet has been credited.");
        } else if (status?.status === "FAILED") {
          MessageUtils.showErrorMessage(message, "Payment failed. Please contact support.");
        } else {
          MessageUtils.showInfoMessage(message, `Payment status: ${status?.status || "pending"}`);
        }
        fetchRecords();
        fetchWalletBalance();
      })
      .catch((error) => {
        setIsCheckingStatus(false);
        setRecordToCheck(null);
        MessageUtils.showErrorMessage(message, error.message || "Failed to check payment status");
      });
  };

  const syncAllPayments = () => {
    setIsSyncing(true);
    new BaseApiServiceImpl("/api/v1/payments/sync")
      .postRequestWithJsonResponse({})
      .then((response: any) => {
        setIsSyncing(false);
        const syncedCount = response?.data ?? 0;
        MessageUtils.showSuccessMessage(message, `Synced ${syncedCount} payment(s). Wallet updated.`);
        fetchRecords();
        fetchWalletBalance();
      })
      .catch((error) => {
        setIsSyncing(false);
        MessageUtils.showErrorMessage(message, error.message || "Failed to sync payments");
      });
  };

  const getStatusSeverity = (status: string) => {
    switch (status) {
      case "Posted":
        return "success";
      case "Reversed":
        return "danger";
      default:
        return "info";
    }
  };

  const breadcrumbHome = {
    icon: "pi pi-home",
    command: () => {
      window.location.href = HOME_ROUTE_PATH;
    },
  };

  const actionBodyTemplate = (rowData: any) => {
    const isReversed = rowData.status === "Reversed";
    const isPendingMobileMoney =
      rowData.mode === "MobileMoneyPayment" &&
      rowData.status !== "Posted" &&
      rowData.status !== "Reversed";
    const isChecking = isCheckingStatus && recordToCheck?.id === rowData.id;

    return (
      <div className="flex align-items-center gap-2">
        {isPendingMobileMoney && (
          <Button
            icon={PrimeIcons.REFRESH}
            className="p-button-sm p-button-info"
            tooltip="Check payment status"
            tooltipOptions={{ position: "top" }}
            onClick={() => checkPaymentStatus(rowData)}
            loading={isChecking}
          />
        )}
        {isSuperAdmin && !isReversed && (
          <Button
            icon={PrimeIcons.REPLAY}
            className="p-button-sm p-button-danger"
            tooltip="Reverse deposit"
            tooltipOptions={{ position: "top" }}
            onClick={() => openReverseDialog(rowData)}
          />
        )}
      </div>
    );
  };

  const manualDepositFooter = (
    <>
      <Button
        label="Cancel"
        icon={PrimeIcons.TIMES}
        className="p-button-text"
        onClick={() => setIsDialogOpen(false)}
      />
      <Button
        label="Save"
        icon={PrimeIcons.SAVE}
        className="p-button-secondary"
        onClick={() => {
          const userDetails = JSON.parse(localStorage.getItem("KEY_USER_DETAILS") || "{}");
          const depositData: any = {
            amount: selectedRecord?.amount,
            transactionReference: selectedRecord?.transactionReference,
            mode: selectedRecord?.mode,
            userAccountId: userDetails?.userId || userDetails?.user?.id,
          };

          new BaseApiServiceImpl("/api/v1/wallet-deposits")
            .postRequestWithJsonResponse(depositData)
            .then(() => {
              MessageUtils.showSuccessMessage(message, "Deposit saved successfully");
              setIsDialogOpen(false);
              fetchRecords();
              fetchWalletBalance();
            })
            .catch((error) => {
              MessageUtils.showErrorMessage(message, error.message);
            });
        }}
      />
    </>
  );

  return (
    <div className="grid">
      <div className="col-12">
        <BreadCrumb home={breadcrumbHome} model={[{ label: "Wallet" }]} />
        <h5>Wallet</h5>
        <Messages ref={message} style={{ width: "100%" }} />
        <Toast ref={toast} />

        {/* Balance and Top-Up Section */}
        <div className="grid mb-3">
          <div className="col-12 md:col-4">
            <div
              className="flex flex-column align-items-center justify-content-center p-3"
              style={{ background: "#f4f6f9", borderRadius: "8px" }}
            >
              <span className="text-500 font-bold">Current Balance</span>
              <span className="text-2xl font-bold mt-2" style={{ color: "#4b79a1" }}>
                {formatAmountWithCommas(walletBalance, "UGX")}
              </span>
              <span className="text-sm mt-1" style={{ color: "#6c757d" }}>
                {smsCredits.toFixed(2)} SMS / {emailCredits.toFixed(2)} Email credits
              </span>
            </div>
          </div>

          <div className="col-12 md:col-8">
            <div className="card">
              <h6 className="mb-3">Top Up Wallet via Mobile Money</h6>
              <div className="grid">
                <div className="col-12 md:col-3">
                  <label htmlFor="amount">Amount (UGX)</label>
                  <InputNumber
                    id="amount"
                    value={amount}
                    onValueChange={(e) => setAmount(Math.round(e.value || 0))}
                    mode="decimal"
                    minFractionDigits={0}
                    maxFractionDigits={0}
                    min={500}
                    placeholder="Enter amount"
                    className="w-full mt-1"
                    disabled={isProcessing}
                  />
                </div>

                <div className="col-12 md:col-3">
                  <label htmlFor="network">Network</label>
                  <Dropdown
                    id="network"
                    value={network}
                    options={NETWORKS}
                    optionValue="value"
                    optionLabel="name"
                    onChange={(e) => setNetwork(e.value)}
                    placeholder="Select network"
                    className="w-full mt-1"
                    disabled={isProcessing}
                  />
                </div>

                <div className="col-12 md:col-3">
                  <label htmlFor="msisdn">Phone Number</label>
                  <div className="p-inputgroup mt-1">
                    <InputText
                      id="msisdn"
                      value={msisdn}
                      onChange={(e) => {
                        setMsisdn(e.target.value);
                        setLookupResult("");
                      }}
                      placeholder="e.g., 256772123456"
                      disabled={isProcessing}
                    />
                    <Button
                      icon="pi pi-search"
                      className="p-button-secondary"
                      onClick={handleLookup}
                      loading={isLookingUp}
                      disabled={isProcessing || !msisdn.trim() || !network}
                      tooltip="Verify account name"
                    />
                  </div>
                  {detectedNetwork && (
                    <small className="block mt-1" style={{ color: "#4b79a1", fontWeight: 500 }}>
                      Detected: {detectedNetwork.network}
                      {detectedNetwork.countryName ? ` (${detectedNetwork.countryName})` : ""}
                    </small>
                  )}
                  {lookupResult && (
                    <small className="p-text-success font-bold mt-1 block">
                      Verified: {lookupResult}
                    </small>
                  )}
                </div>

                <div className="col-12 md:col-3">
                  <label htmlFor="customerName">Account Name</label>
                  <InputText
                    id="customerName"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Your name"
                    className="w-full mt-1"
                    disabled={isProcessing}
                  />
                </div>

                <div className="col-12 md:col-8">
                  <label htmlFor="narration">Narration</label>
                  <InputText
                    id="narration"
                    value={narration}
                    onChange={(e) => setNarration(e.target.value)}
                    placeholder="Payment description"
                    className="w-full mt-1"
                    disabled={isProcessing}
                  />
                </div>

                <div className="col-12 md:col-4 flex align-items-end">
                  <Button
                    label="Pay Now"
                    icon="pi pi-check"
                    className="p-button-success w-full"
                    onClick={handleTopUp}
                    loading={isProcessing}
                    disabled={isProcessing}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Deposit History Section */}
        <div className="card">
          <div className="flex align-items-center justify-content-between mb-3">
            <h6>Deposit History</h6>
            <div className="flex gap-2">
              <Button
                label="Sync Payments"
                icon={PrimeIcons.REFRESH}
                className="p-button-info p-button-sm"
                onClick={syncAllPayments}
                loading={isSyncing}
              />
              {isSuperAdmin && (
                <Button
                  label="New Manual Deposit"
                  icon={PrimeIcons.PLUS}
                  className="p-button-success p-button-sm"
                  onClick={openNewManualDeposit}
                />
              )}
            </div>
          </div>
          <DataTable
            value={records}
            lazy
            paginator
            rows={limit}
            totalRecords={totalRecords}
            loading={loading}
            first={offset}
            rowsPerPageOptions={[5, 10, 25, 50]}
            onPage={onPageChange}
            dataKey="id"
            emptyMessage="No deposits found"
          >
            <Column field="id" header="#" headerStyle={{ width: "60px" }} />
            <Column field="transactionReference" header="Transaction Ref" />
            <Column
              field="amountDeposited"
              header="Amount"
              body={(rowData: any) => formatAmountWithCommas(rowData.amountDeposited, "UGX")}
            />
            <Column
              field="amountRemaining"
              header="Remaining"
              body={(rowData: any) => formatAmountWithCommas(rowData.amountRemaining, "UGX")}
            />
            <Column field="mode" header="Mode" />
            <Column
              field="status"
              header="Status"
              body={(rowData: any) => (
                <Tag value={rowData.status} severity={getStatusSeverity(rowData.status)} />
              )}
            />
            <Column
              field="dateCreated"
              header="Date"
              body={(rowData: any) => rowData.dateCreated?.substring(0, 10)}
            />
            <Column header="Actions" body={actionBodyTemplate} headerStyle={{ width: "120px" }} />
          </DataTable>
        </div>

        {/* Manual Deposit Dialog */}
        <Dialog
          visible={isDialogOpen}
          header="New Manual Deposit"
          modal
          style={{ width: "40vw" }}
          onHide={() => setIsDialogOpen(false)}
          footer={manualDepositFooter}
        >
          <div className="grid">
            <div className="col-12">
              <label htmlFor="amount">Amount</label>
              <InputNumber
                id="amount"
                value={selectedRecord?.amount}
                onValueChange={(e) =>
                  setSelectedRecord((prev: any) => ({ ...prev, amount: Math.round(e.value || 0) }))
                }
                mode="decimal"
                minFractionDigits={0}
                maxFractionDigits={0}
                min={1}
                placeholder="Enter deposit amount"
                className="w-full"
              />
            </div>
            <div className="col-12">
              <label htmlFor="transactionReference">Transaction Reference</label>
              <InputText
                id="transactionReference"
                value={selectedRecord?.transactionReference || ""}
                onChange={(e) =>
                  setSelectedRecord((prev: any) => ({ ...prev, transactionReference: e.target.value }))
                }
                placeholder="Enter transaction reference"
                className="w-full"
              />
            </div>
            <div className="col-12">
              <label htmlFor="mode">Payment Mode</label>
              <Dropdown
                id="mode"
                value={selectedRecord?.mode}
                options={WALLET_DEPOSIT_MODES}
                optionValue="value"
                optionLabel="name"
                onChange={(e) =>
                  setSelectedRecord((prev: any) => ({ ...prev, mode: e.value }))
                }
                placeholder="Select payment mode"
                className="w-full"
              />
            </div>
          </div>
        </Dialog>

        {/* Reverse Deposit Dialog */}
        <Dialog
          visible={reverseDialogVisible}
          header="Reverse Deposit"
          modal
          style={{ width: "40vw" }}
          onHide={() => setReverseDialogVisible(false)}
          footer={
            <>
              <Button
                label="Cancel"
                icon={PrimeIcons.TIMES}
                className="p-button-text"
                onClick={() => setReverseDialogVisible(false)}
              />
              <Button
                label="Reverse"
                icon={PrimeIcons.REPLAY}
                className="p-button-danger"
                onClick={confirmReverse}
                loading={isReversing}
              />
            </>
          }
        >
          <div className="grid">
            <div className="col-12 mb-2">
              <span className="text-500">Transaction:</span>
              <span className="font-bold ml-2">{recordToReverse?.transactionReference}</span>
            </div>
            <div className="col-12 mb-2">
              <span className="text-500">Amount:</span>
              <span className="font-bold ml-2">
                {formatAmountWithCommas(recordToReverse?.amountDeposited, "UGX")}
              </span>
            </div>
            <div className="col-12">
              <label htmlFor="reversalReason">Reversal Reason *</label>
              <InputTextarea
                id="reversalReason"
                value={reversalReason}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setReversalReason(e.target.value)}
                rows={3}
                placeholder="Enter reason for reversal"
                className="w-full mt-1"
              />
            </div>
          </div>
        </Dialog>

        {/* Payment Status Dialog */}
        <Dialog
          header="Payment Status"
          visible={showStatusDialog}
          style={{ width: "40vw" }}
          onHide={() => {
            setShowStatusDialog(false);
            if (pollingRef.current) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
            }
          }}
          footer={
            <Button
              label="Close"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => {
                setShowStatusDialog(false);
                if (pollingRef.current) {
                  clearInterval(pollingRef.current);
                  pollingRef.current = null;
                }
              }}
            />
          }
        >
          {paymentResponse && (
            <div className="grid">
              <div className="col-12 text-center mb-3">
                <i
                  className={`pi ${
                    transactionStatus === "COMPLETED"
                      ? "pi-check-circle text-green-500"
                      : transactionStatus === "FAILED"
                      ? "pi-times-circle text-red-500"
                      : "pi-spin pi-spinner"
                  }`}
                  style={{ fontSize: "3rem" }}
                />
              </div>
              <div className="col-12">
                <div className="mb-2">
                  <span className="text-500">Reference:</span>
                  <span className="font-bold ml-2">{paymentResponse.reference}</span>
                </div>
                <div className="mb-2">
                  <span className="text-500">Amount:</span>
                  <span className="font-bold ml-2">{formatAmountWithCommas(amount || 0, "UGX")}</span>
                </div>
                <div className="mb-2">
                  <span className="text-500">Phone:</span>
                  <span className="font-bold ml-2">{msisdn}</span>
                </div>
                <div className="mb-2">
                  <span className="text-500">Network:</span>
                  <span className="font-bold ml-2">{network}</span>
                </div>
                <div className="mb-2">
                  <span className="text-500">Status:</span>
                  <span
                    className={`font-bold ml-2 ${
                      transactionStatus === "COMPLETED"
                        ? "text-green-500"
                        : transactionStatus === "FAILED"
                        ? "text-red-500"
                        : "text-orange-500"
                    }`}
                  >
                    {transactionStatus?.toUpperCase()}
                  </span>
                </div>
                {transactionStatus === "PENDING" && (
                  <div className="col-12 mt-3">
                    <small className="text-500">
                      Please check your phone and approve the payment prompt. This dialog will update automatically.
                    </small>
                  </div>
                )}
              </div>
            </div>
          )}
        </Dialog>
      </div>
    </div>
  );
};

export default Wallet;
